/**
 * Billing API Client
 * Handles all billing-related API calls to the backend billing service.
 * Routes through the frontend Express proxy at /billing/*
 */

const API_BASE = '/billing';

export interface Plan {
  id: string;
  stripe_price_id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[];
  trial_days: number;
}

export interface Subscription {
  id: string;
  stripe_subscription_id: string;
  status: string;
  plan_name: string;
  amount: number;
  currency: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  features: string[];
  is_trial?: boolean;
  trial_ends_at?: string | null;
  billing_interval?: string;
  discount_percent?: number | null;
  discount_name?: string | null;
  coupon_code?: string | null;
  effective_amount?: number | null;
  pending_downgrade?: {
    plan_name: string;
    amount: number;
    interval: string;
    effective_date: string;
  } | null;
}

export interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  receipt_url: string | null;
  invoice_pdf: string | null;
}

export interface BillingConfig {
  stripe_publishable_key: string;
  enabled: boolean;
  environment: string;
}

export interface CheckoutSession {
  session_id: string;
  url: string;
}

export interface PortalSession {
  url: string;
}

export interface QuotaInfo {
  used: number;
  limit: number;
  unlimited: boolean;
}

export interface QuotaExceededError {
  error: 'quota_exceeded';
  metric: string;
  current: number;
  limit: number;
  plan: string;
  message: string;
}

// Helper to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('dashAccessToken') || localStorage.getItem('authToken');
};

// Helper for authenticated requests
const authFetch = async (url: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(options.headers as Record<string, string> || {}),
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));

    // Detect quota_exceeded 403 and re-throw with structured data
    if (response.status === 403 && error?.detail?.error === 'quota_exceeded') {
      const quotaError = error.detail as QuotaExceededError;
      const err = new Error(quotaError.message || 'Quota exceeded') as Error & { quotaExceeded: QuotaExceededError };
      err.quotaExceeded = quotaError;
      throw err;
    }

    throw new Error(error.detail || 'Request failed');
  }

  return response.json();
};

/**
 * Get public billing configuration
 */
export const getBillingConfig = async (): Promise<BillingConfig> => {
  const response = await fetch(`${API_BASE}/config`);
  if (!response.ok) throw new Error('Failed to fetch billing config');
  return response.json();
};

/**
 * Get available subscription plans
 */
export const getPlans = async (): Promise<Plan[]> => {
  const response = await fetch(`${API_BASE}/plans`);
  if (!response.ok) throw new Error('Failed to fetch plans');
  const data = await response.json();
  return data.plans;
};

/**
 * Get current user's subscription with usage quotas
 */
export const getSubscription = async (): Promise<{
  subscription: Subscription | null;
  is_subscribed: boolean;
  plan: string;
  quotas: Record<string, QuotaInfo>;
  is_trial?: boolean;
  trial_ends_at?: string | null;
  signup_intent?: string | null;
}> => {
  return authFetch(`${API_BASE}/subscription`);
};

/**
 * Create a checkout session for subscription
 */
export const createCheckoutSession = async (
  priceId: string,
  successUrl?: string,
  cancelUrl?: string,
  trialDays?: number
): Promise<CheckoutSession> => {
  const currentUrl = window.location.origin;

  // Check for auto-apply coupon (set by campaign landing pages like /taaft)
  let coupon: string | undefined;
  try {
    const stored = localStorage.getItem('midash_coupon');
    if (stored) coupon = stored;
  } catch {}

  // Detect language for Stripe checkout locale
  const lang = localStorage.getItem('i18nextLng') || 'en';
  const locale = lang.startsWith('ar') ? 'auto' : 'en';

  return authFetch(`${API_BASE}/checkout`, {
    method: 'POST',
    body: JSON.stringify({
      price_id: priceId,
      success_url: successUrl || `${currentUrl}/payment-success`,
      cancel_url: cancelUrl || `${currentUrl}/plans`,
      trial_period_days: trialDays,
      locale,
      ...(coupon ? { coupon } : {}),
    }),
  });
};

/**
 * Create a customer portal session for managing subscription
 */
export const createPortalSession = async (returnUrl?: string): Promise<PortalSession> => {
  const currentUrl = window.location.origin;
  const lang = localStorage.getItem('i18nextLng') || 'en';
  const locale = lang.startsWith('ar') ? 'auto' : 'en';

  return authFetch(`${API_BASE}/portal`, {
    method: 'POST',
    body: JSON.stringify({
      return_url: returnUrl || `${currentUrl}/billing`,
      locale,
    }),
  });
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (atPeriodEnd: boolean = true): Promise<{ message: string }> => {
  return authFetch(`${API_BASE}/cancel`, {
    method: 'POST',
    body: JSON.stringify({
      at_period_end: atPeriodEnd,
    }),
  });
};

/**
 * Submit cancellation exit survey and optionally proceed with cancellation
 */
export const submitCancelSurvey = async (data: {
  reasons: string[];
  other_text?: string;
  offer_shown?: string;
  offer_accepted?: boolean;
  proceed_cancel: boolean;
}): Promise<{ message: string; cancelled: boolean }> => {
  return authFetch(`${API_BASE}/cancel-survey`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Extend current trial by 7 additional days
 */
export const extendTrial = async (): Promise<{ message: string; trial_ends_at: string; days: number }> => {
  return authFetch(`${API_BASE}/extend-trial`, { method: 'POST' });
};

/**
 * Get payment history
 */
export const getPaymentHistory = async (limit: number = 20): Promise<Payment[]> => {
  const data = await authFetch(`${API_BASE}/history?limit=${limit}`);
  return data.payments;
};

/**
 * Get a Stripe Customer Portal URL pre-filled for a specific plan change.
 * Redirects user to Stripe where they see proration preview and confirm.
 */
export const getUpgradePortalUrl = async (newPriceId: string): Promise<{ url: string }> => {
  const locale = document.documentElement.lang || 'en';
  return authFetch(`${API_BASE}/upgrade-portal`, {
    method: 'POST',
    body: JSON.stringify({
      new_price_id: newPriceId,
      return_url: `${window.location.origin}/billing`,
      locale: locale === 'ar' ? 'auto' : 'en',
    }),
  });
};

/**
 * Cancel a scheduled plan downgrade. User stays on their current plan.
 */
export const cancelPendingDowngrade = async (): Promise<{ message: string }> => {
  return authFetch(`${API_BASE}/cancel-downgrade`, {
    method: 'POST',
  });
};

/**
 * Apply retention discount (20% off 3 months) to prevent cancellation
 */
export const applyRetentionDiscount = async (): Promise<{ message: string }> => {
  return authFetch(`${API_BASE}/apply-retention-discount`, {
    method: 'POST',
  });
};

/**
 * Reactivate a cancelled subscription (undo cancel_at_period_end)
 */
export const reactivateSubscription = async (): Promise<{ message: string }> => {
  return authFetch(`${API_BASE}/reactivate`, {
    method: 'POST',
  });
};

/**
 * Change subscription plan directly via API (fallback, no Stripe confirmation page)
 */
export const changePlan = async (newPriceId: string): Promise<{ message: string }> => {
  return authFetch(`${API_BASE}/change-plan`, {
    method: 'POST',
    body: JSON.stringify({ new_price_id: newPriceId }),
  });
};

// NOTE: The old `startFreeTrial()` helper (POST /billing/start-trial) granted a
// Pro trial with NO credit card and has been REMOVED. All trial entry points now
// use `startTrialCheckout()` (utils/trialFlow), which routes through card-gated
// Stripe Checkout. The backend /billing/start-trial endpoint is likewise now
// card-gated (returns a Checkout URL, never a free grant).

/**
 * Redirect to Stripe Checkout.
 * @param priceId - Stripe price ID
 * @param trialDays - If provided, Stripe applies a free trial (credit card still collected)
 * @param opts - Optional success/cancel URL overrides. The card-required trial flow
 *   uses these to land the user back on /platform (trial_started=1) or, on abandon,
 *   /platform (trial_skipped=1) instead of the default /payment-success and /plans.
 */
export const redirectToCheckout = async (
  priceId: string,
  trialDays?: number,
  opts?: { successUrl?: string; cancelUrl?: string }
): Promise<void> => {
  const session = await createCheckoutSession(priceId, opts?.successUrl, opts?.cancelUrl, trialDays);
  window.location.href = session.url;
};

/**
 * Redirect to Stripe Customer Portal
 */
export const redirectToPortal = async (): Promise<void> => {
  const session = await createPortalSession();
  window.location.href = session.url;
};
